import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  InputBase,
  Autocomplete as MuiAutocomplete,
  Popper,
  PopperProps,
  styled,
} from '@mui/material'
import { ReferencedEntity } from '../../../../domain/value-object/ReferencedEntity'
import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDebounce } from '../../../hooks/useDebounce'
import { MenuItem, MenuItemText } from '../../menu'
import { ExpandMoreRounded, CloseRounded } from '@mui/icons-material'
import { colorPalette } from '../../../style/colorPalette'

export type RenderTagsOnMultiAutocomplete<Entity extends ReferencedEntity> = (
  value: Entity[],
  getTagProps: AutocompleteRenderGetTagProps
) => ReactNode
type MultiAutocompleteProps<Entity extends ReferencedEntity> = {
  value: Entity[]
  onChange?: (v: Entity[]) => void
  search: (searchText: string) => Promise<Entity[]>

  renderTags?: RenderTagsOnMultiAutocomplete<Entity>
  renderOption?: (v: ReferencedEntity) => JSX.Element
  renderOptionRaw?: (
    // TODO Remove
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Entity,
    state: AutocompleteRenderOptionState
  ) => JSX.Element
}

export const MultiAutocomplete = <Entity extends ReferencedEntity>({
  value,
  onChange,
  search,
  renderTags,
  renderOption: _renderOption,
  renderOptionRaw,
}: MultiAutocompleteProps<Entity>) => {
  const [inputValue, setInputValue] = useState<string>('')
  const [options, setOptions] = useState<Entity[]>(value || [])
  const [inputChangedBy, setInputChangedBy] =
    useState<AutocompleteInputChangeReason>('reset')
  const onChangeValue = useCallback(
    (_, v: Entity[], reason: AutocompleteChangeReason) => {
      onChange && onChange(v)
    },
    [onChange]
  )
  const onChangeInput = useCallback(
    (_, value: string, reason: AutocompleteInputChangeReason) => {
      setInputValue(value)
      setInputChangedBy(reason)
    },
    []
  )

  const onFocus = useCallback(async () => {
    const response = await search('')
    setOptions(response)
  }, [search])
  const debouncedValue = useDebounce(inputValue, 300)
  useEffect(() => {
    const fn = async () => {
      const response = await search(debouncedValue)
      setOptions(response)
    }
    if (['input', 'clear'].includes(inputChangedBy)) {
      fn()
    }
  }, [debouncedValue, inputChangedBy, search])
  const renderInput = useCallback((params: AutocompleteRenderInputParams) => {
    return <TextInput {...params.InputProps} inputProps={params.inputProps} />
  }, [])
  const renderOption = useCallback((props, value: Entity) => {
    return (
      <MenuItem {...props}>
        {_renderOption ? (
          _renderOption(value)
        ) : (
          <MenuItemText>{value.name}</MenuItemText>
        )}
      </MenuItem>
    )
  }, [])
  const isOptionEqualToValue = useCallback((option: Entity, value: Entity) => {
    return option.uuid === value.uuid
  }, [])
  const getOptionLabel = useCallback((option: Entity) => {
    return option.name
  }, [])
  const PopperComponent = memo((props: PopperProps) => (
    <Popper {...props} placement="bottom-start" />
  ))
  const selectedValues = useMemo(() => value || [], [value])
  return (
    <MuiAutocomplete
      // Use sx props since making Autocomplete styled component takes a bit effort.
      sx={{ width: '100%' }}
      multiple={true}
      value={selectedValues}
      onChange={onChangeValue}
      isOptionEqualToValue={isOptionEqualToValue}
      options={options}
      renderInput={renderInput}
      inputValue={inputValue}
      onInputChange={onChangeInput}
      onFocus={onFocus}
      getOptionLabel={getOptionLabel}
      renderOption={renderOptionRaw || renderOption}
      disableCloseOnSelect={true}
      PopperComponent={PopperComponent}
      popupIcon={<PopupIcon />}
      clearIcon={<CloseIcon />}
      renderTags={renderTags}
      filterOptions={opts => opts}
    />
  )
}

const TextInput = styled(InputBase)({
  width: '100%',
  color: colorPalette.monotone[10],
})
const PopupIcon = styled(ExpandMoreRounded)({
  color: colorPalette.monotone[4],
})
const CloseIcon = styled(CloseRounded)({
  color: colorPalette.monotone[4],
  fontSize: '14px',
})
